import React from 'react';
import { Box, Typography, Button, CircularProgress, useTheme } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { ILogPremium } from '../types';
import { formatarDataHora } from '../utils';

interface LinhaTempoLogsProps {
  logs: ILogPremium[];
  selecionadoLog: ILogPremium | null;
  onSelecionarLog: (log: ILogPremium) => void;
  carregandoMais: boolean;
  temMais: boolean;
  onCarregarMais: () => void;
}

const obterIconeSeveridade = (sev: string) => {
  const norm = sev.toLowerCase();
  switch (norm) {
    case 'erro':
    case 'critico':
    case 'critical':
    case 'error':
      return <ErrorIcon sx={{ color: '#EF4444' }} />;
    case 'alerta':
    case 'warning':
    case 'attention':
    case 'atencao':
      return <WarningIcon sx={{ color: '#F59E0B' }} />;
    case 'sucesso':
    case 'success':
    case 'concluido':
      return <CheckCircleIcon sx={{ color: '#10B981' }} />;
    case 'informativo':
    case 'info':
    case 'information':
    default:
      return <InfoIcon sx={{ color: '#3B82F6' }} />;
  }
};

export const LinhaTempoLogs: React.FC<LinhaTempoLogsProps> = ({
  logs,
  selecionadoLog,
  onSelecionarLog,
  carregandoMais,
  temMais,
  onCarregarMais
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {logs.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Nenhum log registrado para este escopo.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pl: 3.5, borderLeft: `2px dashed ${theme.palette.divider}`, ml: 1.5, py: 1 }}>
          {logs.map((log, index) => {
            const isSelecionado = selecionadoLog?.logID === log.logID;
            return (
              <Box
                key={log.logID}
                onClick={() => onSelecionarLog(log)}
                sx={{
                  position: 'relative',
                  mb: index === logs.length - 1 ? 0 : 3,
                  cursor: 'pointer',
                  backgroundColor: isSelecionado 
                    ? (theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.15)' : 'rgba(42, 81, 247, 0.08)')
                    : theme.palette.background.paper,
                  border: isSelecionado ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: '10px',
                  p: 2,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: isSelecionado ? theme.palette.primary.main : theme.palette.primary.light,
                    backgroundColor: isSelecionado 
                      ? (theme.palette.mode === 'dark' ? 'rgba(42, 81, 247, 0.2)' : 'rgba(42, 81, 247, 0.12)')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)')
                  }
                }}
              >
                {/* Ícone posicionado na linha do tempo */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '-40px',
                    top: '18px',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  {obterIconeSeveridade(log.severidade)}
                </Box>

                {/* Conteúdo do Log */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {log.titulo}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {formatarDataHora(log.dataHora)}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem'
                  }}
                >
                  {log.mensagem}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    Origem: {log.origem}
                  </Typography>
                  {log.detalhes && (
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Possui detalhes técnicos
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {temMais && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          {carregandoMais ? (
            <CircularProgress size={24} />
          ) : (
            <Button
              onClick={onCarregarMais}
              variant="outlined"
              size="small"
              sx={{
                borderColor: 'divider',
                color: 'text.secondary',
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(42, 81, 247, 0.04)',
                  color: 'primary.main'
                }
              }}
            >
              Carregar mais logs
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LinhaTempoLogs;
